const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

export const leadService = {
    getLeads: async ({ page = 1, limit = 10, filterKey, status, source, stage, visitType, followupFilter, sortOrder, assignmentFilter, associateName, associateId, provider, date, fromDate, toDate }) => {
        const params = new URLSearchParams({
            page,
            limit,
        });

        if (provider) {
            params.append('provider', provider);
        }

        if (associateName) {
            params.append('associateName', associateName);
        }

        if (associateId) {
            params.append('associateId', associateId);
        }

        if (sortOrder) {
            params.append('sortOrder', sortOrder);
        }

        if (filterKey && filterKey.trim()) {
            params.append('filterKey', filterKey.trim());
        }

        if (status && status !== 'all' && status !== 'All Leads') {
            params.append('status', status);
        }

        if (source && source !== 'all') {
            params.append('source', source);
        }

        if (stage && stage !== 'all') {
            params.append('stage', stage);
        }

        if (visitType) {
            params.append('visitType', visitType);
        }

        if (followupFilter) {
            params.append('followupFilter', followupFilter);
        }

        if (assignmentFilter) {
            params.append('assignmentFilter', assignmentFilter);
        }

        if (date) {
            params.append('date', date);
        }

        if (fromDate) {
            params.append('fromDate', fromDate);
        }

        if (toDate) {
            params.append('toDate', toDate);
        }

        const token = typeof localStorage !== 'undefined' ? localStorage.getItem('token') : null;
        const headers = { 'Content-Type': 'application/json' };
        if (token) {
            headers['Authorization'] = `Bearer ${token}`;
        }

        const response = await fetch(`${API_BASE_URL}/api/leads/hierarchy-wise?${params.toString()}`, {
            method: 'GET',
            headers,
        });

        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const json = await response.json();
        if (json.status !== true) throw new Error(json.message || 'API error');
        return json.data;
    },

    deleteLead: async (leadId) => {
        const response = await fetch(`${API_BASE_URL}/api/leads/${leadId}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ isDeleted: true }),
        });
        if (!response.ok) throw new Error('Failed to delete lead');
        return response.json();
    },

    getTimeline: async (leadId) => {
        const response = await fetch(`${API_BASE_URL}/api/leads/timeline/${leadId}`, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' },
        });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const json = await response.json();
        return Array.isArray(json?.data) ? json.data : [];
    },

    saveFollowUp: async (payload) => {
        const response = await fetch(`${API_BASE_URL}/api/leads/followup`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
        });
        if (!response.ok) {
            const err = await response.json().catch(() => ({}));
            throw new Error(err.message || `HTTP ${response.status}`);
        }
        return response.json();
    },

    transferLead: async (payload) => {
        const response = await fetch(`${API_BASE_URL}/api/leads/transfer`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
        });
        if (!response.ok) throw new Error('Transfer failed');
        return response.json();
    },

    changeAssociate: async (payload) => {
        const response = await fetch(`${API_BASE_URL}/api/leads/change-associate`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
        });
        if (!response.ok) {
            const err = await response.json().catch(() => ({}));
            throw new Error(err.message || `HTTP ${response.status}`);
        }
        const json = await response.json();
        if (json.status !== true) throw new Error(json.message || 'API error');
        return json;
    },

    createLead: async (payload) => {
        const response = await fetch(`${API_BASE_URL}/api/leads`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
        });
        if (!response.ok) {
            const err = await response.json().catch(() => ({}));
            throw new Error(err.message || `HTTP ${response.status}`);
        }
        return response.json();
    },

    getLeadActivities: async (leadId, { page = 1, limit = 20 } = {}) => {
        const params = new URLSearchParams({
            page,
            limit,
        });

        const response = await fetch(`${API_BASE_URL}/api/leads/activities/${leadId}?${params.toString()}`, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' },
        });

        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const json = await response.json();
        if (json.status !== true) throw new Error(json.message || 'API error');
        return json.data;
    },

    getLeadsByAssociate: async (associateId, { page = 1, limit = 10 } = {}) => {
        const params = new URLSearchParams({
            page,
            limit,
        });

        if (associateId) {
            params.append('associateId', associateId);
        }

        const response = await fetch(`${API_BASE_URL}/api/leads/by-associate?${params.toString()}`, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' },
        });

        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const json = await response.json();
        if (json.status !== true) throw new Error(json.message || 'API error');
        return json.data;
    },

    getLeadRouting: async () => {
        const token = typeof localStorage !== 'undefined' ? localStorage.getItem('token') : null;
        const headers = { 'Content-Type': 'application/json' };
        if (token) {
            headers['Authorization'] = `Bearer ${token}`;
        }
        const response = await fetch(`${API_BASE_URL}/api/lead-routing`, {
            method: 'GET',
            headers,
        });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.json();
    },

    getLeadRoutingDashboard: async () => {
        const token = typeof localStorage !== 'undefined' ? localStorage.getItem('token') : null;
        const headers = { 'Content-Type': 'application/json' };
        if (token) {
            headers['Authorization'] = `Bearer ${token}`;
        }
        const response = await fetch(`${API_BASE_URL}/api/lead-routing/dashboard`, {
            method: 'GET',
            headers,
        });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.json();
    },

    updateLeadRouting: async (payload) => {
        const token = typeof localStorage !== 'undefined' ? localStorage.getItem('token') : null;
        const headers = { 'Content-Type': 'application/json' };
        if (token) {
            headers['Authorization'] = `Bearer ${token}`;
        }
        const response = await fetch(`${API_BASE_URL}/api/lead-routing`, {
            method: 'PATCH',
            headers,
            body: JSON.stringify(payload),
        });
        if (!response.ok) {
            const err = await response.json().catch(() => ({}));
            throw new Error(err.message || `HTTP ${response.status}`);
        }
        return response.json();
    },
};
